import axios from "@/utils/axios";
import {
  QueryClient,
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";

import type {
  ApiEntityResponseType,
  EntityType,
  EntityTypeResponse,
} from "@/schemas/digital-files";

export type FiltersType = {
  filter?: { [key: string]: string };
  order_by?: string;
  page?: number;
  per_page?: number;
};

export const useGetEntities = (params?: FiltersType) => {
  const { filter, ...rest } = params || {};
  const { status, search, queryFilters } = filter || {};

  const lastStatus = status?.split(",").pop();

  const queryParams = {
    ...rest,
    filter: queryFilters,
    ...(lastStatus && { status: lastStatus }),
    search: search || undefined,
  };

  return useQuery<ApiEntityResponseType>({
    queryKey: ["entity-list", queryParams],
    queryFn: async () => {
      const response = await axios.get<ApiEntityResponseType>(
        `/admin/v1/expedient-base-entities`,
        {
          params: { ...queryParams, current_version_only: 1 },
        },
      );
      return response.data;
    },
  });
};

interface UseGetEntityOptions {
  includeFields?: boolean;
  includeVersionList?: boolean;
  includeFormalityRequests?: boolean;
  includeRoles?: boolean;
}

export const getEntity = async (
  id: string,
  options: UseGetEntityOptions = {},
): Promise<EntityTypeResponse | undefined> => {
  const {
    includeFields,
    includeVersionList,
    includeFormalityRequests,
    includeRoles,
  } = options;
  const response = await axios.get<EntityTypeResponse>(
    `/admin/v1/expedient-base-entities/${id}`,
    {
      params: {
        include_fields: includeFields ? 1 : 0,
        include_version_list: includeVersionList ? 1 : 0,
        include_formality_requests: includeFormalityRequests ? 1 : 0,
        include_roles: includeRoles ? 1 : 0,
      },
    },
  );
  return response.data;
};

export const useGetEntity = (
  id: string = "",
  options: UseGetEntityOptions = {},
  enabled: boolean = true,
) => {
  const shouldFetch = !!id && enabled;
  return useQuery<EntityType | undefined>({
    queryKey: ["entity-detail", id, options],
    queryFn: async () => {
      const response = await getEntity(id, options);
      return response?.data;
    },
    enabled: shouldFetch,
  });
};

export const createEntityDetailsBreadcrumb = (
  queryClient: QueryClient,
  id: string,
) => {
  return async () => {
    try {
      const queryKey = ["entity-detail", id];
      if (!id) {
        return { data: null };
      }
      const response = await queryClient.ensureQueryData({
        queryKey,
        queryFn: async () => getEntity(id),
        staleTime: 5 * 60 * 1000,
      });

      return { data: response?.data };
    } catch (error) {
      return {
        error: error instanceof Error ? error.message : "Unknown error",
      };
    }
  };
};

export const useSaveNewEntity = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: Partial<EntityType>) => {
      const response = await axios.post(`/admin/v1/expedient-base-entities`, {
        ...data,
      });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["entity-list"],
      });
      queryClient.invalidateQueries({
        queryKey: ["entity-selector-infinite"],
      });
    },
  });
};

export const useUpdateEntity = (id: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: Partial<EntityType>) => {
      const response = await axios.put(
        `/admin/v1/expedient-base-entities/${id}`,
        {
          ...data,
        },
      );
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["entity-list"],
      });
      queryClient.resetQueries({ queryKey: ["admin-flow-detail"] });
      queryClient.resetQueries({
        queryKey: ["admin-node-id"],
      });
      queryClient.resetQueries({
        queryKey: ["admin-flow-details"],
      });
      queryClient.resetQueries({
        queryKey: ["entity-detail"],
      });
      queryClient.refetchQueries({
        queryKey: ["entity-selector-infinite"],
      });
      queryClient.resetQueries({ queryKey: ["admin-form"] });
      queryClient.resetQueries({ queryKey: ["form"] });
      queryClient.resetQueries({ queryKey: ["expedientFields"] });
    },
  });
};

export const useUpdateEntityStatus = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, activate }: { id: string; activate: boolean }) => {
      const response = await axios.post(
        `/admin/v1/expedient-base-entities/${id}/${activate ? "activate" : "deactivate"}`,
      );
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["entity-list"],
      });
      queryClient.invalidateQueries({
        queryKey: ["entity-selector-infinite"],
      });
      queryClient.resetQueries({ queryKey: ["admin-flow-detail"] });
      queryClient.resetQueries({
        queryKey: ["admin-node-id"],
      });
      queryClient.resetQueries({
        queryKey: ["admin-flow-details"],
      });
      queryClient.resetQueries({
        queryKey: ["entity-detail"],
      });
      queryClient.resetQueries({ queryKey: ["admin-form"] });
      queryClient.resetQueries({ queryKey: ["form"] });
      queryClient.resetQueries({ queryKey: ["expedientFields"] });
    },
  });
};

export const useGetEntitiesInfinite = (
  params?: Pick<FiltersType, "filter">,
) => {
  const { filter } = params || {};
  const { search, ...restFilters } = filter || {};

  // Si hay búsqueda, no usar paginación y retornar todos los resultados
  const shouldPaginate = !search;

  return useInfiniteQuery({
    queryKey: ["entity-selector-infinite", filter],
    initialPageParam: 1,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    refetchOnWindowFocus: false,
    queryFn: async ({ pageParam }): Promise<ApiEntityResponseType> => {
      const response = await axios.get<ApiEntityResponseType>(
        "/admin/v1/expedient-base-entities",
        {
          params: {
            ...(shouldPaginate && { page: pageParam, per_page: 15 }),
            ...restFilters,
            order_by: "!created_at",
            // Con status BE ya resuelve la versión; combinarlo con current_version_only devuelve vacío
            ...(!restFilters.status && {
              current_version_only: 1,
              include_active_version: 1,
            }),
            search: search || undefined,
          },
        },
      );
      return response.data;
    },
    getNextPageParam: (lastPage) => {
      // Si hay búsqueda activa, no hay siguiente página
      if (search) return undefined;

      const currentPage = lastPage.meta.current_page;
      const lastPageNum = lastPage.meta.last_page;
      return currentPage < lastPageNum ? currentPage + 1 : undefined;
    },
  });
};

interface ExpedientBaseEntityField {
  id: string | number;
  name: string;
  field_type: string;
  configuration?: {
    key?: string | null;
  };
}

export const getExpedientFields = async (
  expedientId: string,
  queryClient: QueryClient,
) => {
  return queryClient.fetchQuery({
    queryKey: ["expedientFields", expedientId],
    queryFn: async () => {
      const response = await axios.get(
        `/admin/v1/expedient-base-entities/${expedientId}`,
        {
          params: {
            include_fields: 1,
          },
        },
      );
      return (response.data?.data?.fields || []) as ExpedientBaseEntityField[];
    },
  });
};
